import { BizAppConfig } from 'config/application.manifest';
import { createAppRequest } from '@/services/request';

const OPEN_PLATFORM_TOKEN_STORAGE_KEY = 'openPlatformAccessToken';
export const CORAL_PLATFORM_SERVICE_CODE = 'coral-platform-management';
const TOKEN_REFRESH_BUFFER_MS = 60 * 1000;
const request = createAppRequest();
let tokenRequestPromise: Promise<OpenPlatformTokenResult> | null = null;
let tokenRequestGeneration = 0;

export interface OpenPlatformTokenResult {
  accessToken: string;
  expiresAt: number;
}

export const getCachedOpenPlatformToken =
  (): OpenPlatformTokenResult | null => {
    try {
      const rawValue = sessionStorage.getItem(OPEN_PLATFORM_TOKEN_STORAGE_KEY);
      if (!rawValue) {
        return null;
      }

      const value = JSON.parse(rawValue) as OpenPlatformTokenResult;
      if (
        typeof value.accessToken !== 'string' ||
        !value.accessToken.trim() ||
        typeof value.expiresAt !== 'number' ||
        value.expiresAt <= Date.now() + TOKEN_REFRESH_BUFFER_MS
      ) {
        sessionStorage.removeItem(OPEN_PLATFORM_TOKEN_STORAGE_KEY);
        return null;
      }

      return value;
    } catch {
      sessionStorage.removeItem(OPEN_PLATFORM_TOKEN_STORAGE_KEY);
      return null;
    }
  };

export const getOpenPlatformToken = (): string | null =>
  getCachedOpenPlatformToken()?.accessToken || null;

export const getOpenPlatformGatewayUrl = (serviceCode: string): string => {
  const baseUrl = BizAppConfig.openPlatformGatewayBaseUrl?.replace(/\/$/, '');
  const gatewayPath = (
    BizAppConfig.openPlatformGatewayPath || '/api-gateway'
  ).replace(/\/$/, '');
  if (!baseUrl) {
    throw new Error('OPEN_SERVICE_URL 未配置');
  }
  return `${baseUrl}${gatewayPath}/${serviceCode}`;
};

export const fetchOpenPlatformToken = (
  forceRefresh = false,
): Promise<OpenPlatformTokenResult> => {
  if (forceRefresh) {
    clearOpenPlatformToken();
  } else {
    const cachedToken = getCachedOpenPlatformToken();
    if (cachedToken) {
      return Promise.resolve(cachedToken);
    }
  }
  if (!forceRefresh && tokenRequestPromise) {
    return tokenRequestPromise;
  }

  const requestGeneration = tokenRequestGeneration;
  let pending: Promise<OpenPlatformTokenResult>;
  pending = request<OpenPlatformTokenResult>('/bff/open-platform/token', {
    method: 'GET',
  })
    .then((result) => {
      if (!result?.accessToken || !result.expiresAt) {
        throw new Error('开放平台令牌响应无效');
      }
      if (requestGeneration === tokenRequestGeneration) {
        sessionStorage.setItem(
          OPEN_PLATFORM_TOKEN_STORAGE_KEY,
          JSON.stringify(result),
        );
      }
      return result;
    })
    .finally(() => {
      if (tokenRequestPromise === pending) {
        tokenRequestPromise = null;
      }
    });

  tokenRequestPromise = pending;
  return pending;
};

export const clearOpenPlatformToken = (): void => {
  tokenRequestGeneration += 1;
  tokenRequestPromise = null;
  sessionStorage.removeItem(OPEN_PLATFORM_TOKEN_STORAGE_KEY);
};
